import { isAxiosError } from 'axios';
import api from './axiosInstance';
import type {
    BulkMatchCommitBody,
    BulkMatchCommitResult,
    BulkMatchPreview,
    DayPlanTemplate,
    DayPlanTemplateVersion,
    GenerateResult,
    GenerationPreview,
    PutMappingInput,
    QtyUnit,
    RegenerateResult,
    RegenerationDiff,
    TaskTemplateMapping,
    TemplateSuggestionsResponse,
    UpsertTemplateInput,
} from '../features/epc/templates/types';

const BASE = '/epc/day-plan-templates';

export async function listDayPlanTemplates(params: {
    disciplineKey?: string;
    status?: string;
    includeArchived?: boolean;
}): Promise<DayPlanTemplate[]> {
    const r = await api.get(BASE, { params });
    return r.data.data.templates;
}

export async function getDayPlanTemplate(templateId: string): Promise<DayPlanTemplate> {
    const r = await api.get(`${BASE}/${templateId}`);
    return r.data.data.template;
}

export async function createDayPlanTemplate(input: UpsertTemplateInput): Promise<DayPlanTemplate> {
    const r = await api.post(BASE, input);
    return r.data.data.template;
}

export async function updateDayPlanTemplate(
    templateId: string,
    input: Partial<UpsertTemplateInput>
): Promise<DayPlanTemplate> {
    const r = await api.patch(`${BASE}/${templateId}`, input);
    return r.data.data.template;
}

export async function archiveDayPlanTemplate(templateId: string, force = false): Promise<DayPlanTemplate> {
    const r = await api.delete(`${BASE}/${templateId}`, { params: force ? { force: true } : {} });
    return r.data.data.template;
}

export async function publishDayPlanTemplate(
    templateId: string,
    changeNote?: string
): Promise<DayPlanTemplateVersion> {
    const r = await api.post(`${BASE}/${templateId}/publish`, changeNote ? { changeNote } : {});
    return r.data.data.version;
}

export async function listTemplateVersions(templateId: string): Promise<DayPlanTemplateVersion[]> {
    const r = await api.get(`${BASE}/${templateId}/versions`);
    return r.data.data.versions;
}

export async function listQtyUnits(): Promise<QtyUnit[]> {
    const r = await api.get('/epc/qty-units');
    return r.data.data.units;
}

const taskBase = (projectId: string, taskKey: string) =>
    `/epc/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}`;

export async function getTemplateSuggestions(
    projectId: string,
    taskKey: string
): Promise<TemplateSuggestionsResponse> {
    const r = await api.get(`${taskBase(projectId, taskKey)}/template-suggestions`);
    return r.data.data;
}

/** 404 = simply unmapped — normalized to null so hooks can treat it as data. */
export async function getTaskTemplateMapping(
    projectId: string,
    taskKey: string
): Promise<TaskTemplateMapping | null> {
    try {
        const r = await api.get(`${taskBase(projectId, taskKey)}/template-mapping`);
        return r.data.data.mapping;
    } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
    }
}

export async function putTaskTemplateMapping(
    projectId: string,
    taskKey: string,
    input: PutMappingInput
): Promise<TaskTemplateMapping> {
    const r = await api.put(`${taskBase(projectId, taskKey)}/template-mapping`, input);
    return r.data.data.mapping;
}

export async function previewTemplateGeneration(
    projectId: string,
    taskKey: string
): Promise<GenerationPreview> {
    const r = await api.post(`${taskBase(projectId, taskKey)}/template-mapping/preview`);
    return r.data.data.preview;
}

export async function generateTemplateActivities(
    projectId: string,
    taskKey: string,
    generationId: string
): Promise<GenerateResult> {
    const r = await api.post(`${taskBase(projectId, taskKey)}/template-mapping/generate`, {
        generationId,
    });
    return r.data.data;
}

export async function unmapTaskTemplate(
    projectId: string,
    taskKey: string,
    deleteCleanFutureDays = false
): Promise<void> {
    await api.delete(`${taskBase(projectId, taskKey)}/template-mapping`, {
        params: deleteCleanFutureDays ? { deleteCleanFutureDays: true } : {},
    });
}

export async function previewRegeneration(
    projectId: string,
    taskKey: string,
    acceptTemplateVersion?: number
): Promise<RegenerationDiff> {
    const r = await api.post(
        `${taskBase(projectId, taskKey)}/template-mapping/regenerate-preview`,
        acceptTemplateVersion !== undefined ? { acceptTemplateVersion } : {}
    );
    return r.data.data.diff;
}

export async function regenerateActivities(
    projectId: string,
    taskKey: string,
    body: { generationId: string; acceptTemplateVersion?: number }
): Promise<RegenerateResult> {
    const r = await api.post(`${taskBase(projectId, taskKey)}/template-mapping/regenerate`, body);
    return r.data.data;
}

export async function dismissReviewFlag(
    projectId: string,
    taskKey: string,
    entry: { date: string; reason: string }
): Promise<TaskTemplateMapping> {
    const r = await api.patch(`${taskBase(projectId, taskKey)}/template-mapping/review-flags`, entry);
    return r.data.data.mapping;
}

export async function listTaskTemplateMappings(
    projectId: string,
    opts: { drift?: boolean } = {}
): Promise<TaskTemplateMapping[]> {
    const r = await api.get(`/epc/projects/${projectId}/template-mappings`, {
        params: opts.drift ? { drift: true } : {},
    });
    return r.data.data.mappings;
}

// ── Bulk template auto-map (Feature A) ───────────────────────────────────────

/** Pure preview (no writes) — scores snapshot leaves against the published library. */
export async function previewBulkMatch(projectId: string, importId?: string): Promise<BulkMatchPreview> {
    const r = await api.post(
        `/epc/projects/${projectId}/template-match/preview`,
        importId ? { importId } : {}
    );
    return r.data.data.preview;
}

/** Commit — bulkMatchId gate (server recomputes; 409 BULKMATCH_STALE on drift). */
export async function commitBulkMatch(
    projectId: string,
    body: BulkMatchCommitBody
): Promise<BulkMatchCommitResult> {
    const r = await api.post(`/epc/projects/${projectId}/template-match/commit`, body);
    return r.data.data;
}
